import { useEffect, useRef, useState } from "react";

function useInView(ref) {
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        if (!ref.current) return;
        const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.3 });
        obs.observe(ref.current);
        return () => obs.disconnect();
    }, [ref]);
    return visible;
}

function Counter({ to, prefix = "", suffix = "", duration = 1800, decimals = 0 }) {
    const ref = useRef(null);
    const visible = useInView(ref);
    const [value, setValue] = useState(0);
    useEffect(() => {
        if (!visible) return;
        const start = performance.now();
        let raf;
        const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setValue(eased * to);
            if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [visible, to, duration]);
    const formatted = decimals > 0 ? value.toFixed(decimals) : Math.round(value).toLocaleString("en-GB");
    return <span ref={ref}>{prefix}{formatted}{suffix}</span>;
}

const STATS = [
    { label: "Recovered for clients", value: 218, prefix: "£", suffix: "m+", note: "Combined client compensation since 2018" },
    { label: "Successful claims won", value: 47800, suffix: "+", note: "Across 10 specialist UK practice areas" },
    { label: "Success rate", value: 94, suffix: "%", note: "Of eligible claims taken on" },
    { label: "Avg. resolution time", value: 11, suffix: " wks", note: "From application to compensation" },
];

export default function StatsCounter() {
    return (
        <section className="bg-navy-900 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-mesh-emerald opacity-90" />
            <div className="absolute inset-0 grid-bg-dark opacity-20" />

            <div className="container-x relative py-20 lg:py-28">
                <div className="max-w-2xl">
                    <span className="text-eyebrow-light">By the numbers</span>
                    <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight">
                        Built on results, <span className="text-emerald-400">not promises.</span>
                    </h2>
                </div>

                <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
                    {STATS.map((s) => (
                        <div key={s.label} className="rounded-2xl bg-white/[0.04] border border-white/10 backdrop-blur-sm p-7 hover:border-emerald-400/40 transition-colors">
                            <div className="text-4xl sm:text-5xl font-display font-bold text-white tracking-tight">
                                <Counter to={s.value} prefix={s.prefix || ""} suffix={s.suffix || ""} />
                            </div>
                            <div className="mt-3 text-sm font-semibold text-emerald-400">{s.label}</div>
                            <div className="mt-1 text-xs text-slate-400 leading-relaxed">{s.note}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
